import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import CommandPalette from './components/CommandPalette';
import ErrorBoundary from './components/ErrorBoundary';
import ToastProvider from './components/Toast';
import { IS_PROD } from './env';
import { PATHS } from './paths';
import AuthProvider from './providers/AuthProvider';
import AlphaAllowListScene from './scenes/AlphaAllowList/AlphaAllowListScene';
import AssistantFeedbackScene from './scenes/Assistant/AssistantFeedbackScene';
import AssistantMemoryScene from './scenes/Assistant/AssistantMemoryScene';
import AssistantScene from './scenes/Assistant/AssistantScene';
import AssistantSessionsScene from './scenes/Assistant/AssistantSessionsScene';
import BankConnectionsScene from './scenes/BankConnections/BankConnectionsScene';
import BillManagementScene from './scenes/BillManagement/BillManagementScene';
import DeleteAccountScene from './scenes/DeleteAccount/DeleteAccountScene';
import EditFeatureGateScene from './scenes/FeatureGates/EditFeatureGateScene';
import FeatureGatesScene from './scenes/FeatureGates/FeatureGatesScene';
import NewFeatureGateScene from './scenes/FeatureGates/NewFeatureGateScene';
import HomeScene from './scenes/HomeScene';
import ImpersonateUserScene from './scenes/ImpersonateUser/ImpersonateUserScene';
import ObjectInspectorScene from './scenes/ObjectInspector/ObjectInspectorScene';
import ProcessorStatusesScene from './scenes/ProcessorStatuses/ProcessorStatusesScene';
import ReleasesScene from './scenes/Releases/ReleasesScene';
import TransactionAnomalyFeedbackScene from './scenes/TransactionAnomalyFeedback/TransactionAnomalyFeedbackScene';
import UsersWithTransactionReviewsScene from './scenes/UsersWithTransactionReviews/UsersWithTransactionReviewsScene';

/**
 * Top-level router + auth shell. Admin tool routes are added incrementally as
 * each tool migrates over from `@orn/web` — see ORN-526 for the batched
 * migration strategy. `CommandPalette` is mounted once here so Cmd+K works
 * from every scene.
 */
const MainApp = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <ToastProvider>
            <CommandPalette />
            <Routes>
              <Route path={PATHS.HOME} element={<HomeScene />} />
              <Route path={PATHS.ALPHA_ALLOW_LIST} element={<AlphaAllowListScene />} />
              <Route path={PATHS.ASSISTANT} element={<AssistantScene />}>
                <Route index element={<Navigate to={PATHS.ASSISTANT_FEEDBACK} replace />} />
                <Route path="memory" element={<AssistantMemoryScene />} />
                <Route path="sessions" element={<AssistantSessionsScene />} />
                <Route path="feedback" element={<AssistantFeedbackScene />} />
              </Route>
              <Route path={PATHS.BANK_CONNECTIONS} element={<BankConnectionsScene />} />
              <Route path={PATHS.BILL_MANAGEMENT} element={<BillManagementScene />} />
              <Route path={PATHS.DELETE_ACCOUNT} element={<DeleteAccountScene />} />
              <Route path={PATHS.FEATURE_GATES} element={<FeatureGatesScene />} />
              <Route path={PATHS.FEATURE_GATE_NEW} element={<NewFeatureGateScene />} />
              <Route path={PATHS.FEATURE_GATE_EDIT} element={<EditFeatureGateScene />} />
              <Route path={PATHS.IMPERSONATE_USER} element={<ImpersonateUserScene />} />
              <Route path={PATHS.OBJECT_INSPECTOR} element={<ObjectInspectorScene />} />
              <Route path={PATHS.PROCESSOR_STATUSES} element={<ProcessorStatusesScene />} />
              {!IS_PROD && <Route path={PATHS.RELEASES} element={<ReleasesScene />} />}
              <Route path={PATHS.TRANSACTION_ANOMALY_FEEDBACK} element={<UsersWithTransactionReviewsScene />} />
              <Route path={PATHS.TRANSACTION_ANOMALY_FEEDBACK_FOR_USER} element={<TransactionAnomalyFeedbackScene />} />
              <Route path="*" element={<Navigate to={PATHS.HOME} replace />} />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default MainApp;
